import { useCallback, useState, useMemo } from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { Check, ChevronsUpDown, Plus, Search, X } from "lucide-react";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

import { cn } from "@/lib/utils";
import { AddOptionDialog } from "./add-option-dialog";

export type SelectSearchableOption<T = unknown> = {
  value: string | number;
  label: string;
  raw?: T;
};

export type FormSelectSearchableProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  options: SelectSearchableOption[];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  description?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  disabled?: boolean;
  onValueChange?: (value: string | number | null) => void;
  preserveMessageSpace?: boolean;
  allowClear?: boolean;
  onAdd?: (searchText: string, resetValue: () => void) => void;
  addButtonLabel?: string;
  addDialogTitle?: string;
  addDialogInputLabel?: string;
  isAddLoading?: boolean;
  required?: boolean;
};

export const FormSelectSearchable = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  control,
  label,
  name,
  description,
  wrapperClassName,
  labelClassName,
  triggerClassName,
  contentClassName,
  descriptionClassName,
  messageClassName,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  disabled,
  onValueChange,
  preserveMessageSpace = false,
  allowClear = true,
  onAdd,
  addButtonLabel = "Add",
  addDialogTitle = "",
  addDialogInputLabel = "",
  isAddLoading = false,
  required,
  ...props
}: FormSelectSearchableProps<TFieldValues, TName>) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogInitialValue, setAddDialogInitialValue] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [options, search]);

  const getOptionLabel = useCallback(
    (value: string | number | null | undefined) => {
      if (value === null || value === undefined || value === "") return null;
      return options.find(
        (option) => option.value.toString() === value.toString(),
      )?.label;
    },
    [options],
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem
          className={cn("aero:w-full", wrapperClassName)}
          aria-invalid={!!fieldState?.error}
        >
          {label && (
            <FormLabel
              className={cn(
                required && "aero:flex aero:items-center aero:justify-between",
                labelClassName,
              )}
            >
              {label}
              {required && (
                <span className="aero:text-destructive" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>
          )}
          <FormControl>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger
                disabled={disabled}
                className={cn(disabled && "aero:cursor-not-allowed")}
              >
                <div
                  role="combobox"
                  tabIndex={disabled ? -1 : 0}
                  aria-expanded={open}
                  aria-disabled={disabled}
                  className={cn(
                    // Base styles matching input field from Figma
                    "aero:flex aero:w-full aero:h-10 aero:items-center aero:justify-between",
                    "aero:px-4 aero:py-3 aero:gap-2",
                    "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                    "aero:shadow-none",
                    "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
                    // Focus and active states
                    "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
                    "aero:data-[state=open]:border-ring aero:data-[state=open]:ring-ring/50 aero:data-[state=open]:ring-[3px]",
                    // Disabled state
                    disabled && "aero:opacity-50 aero:cursor-not-allowed",
                    // Error state
                    fieldState?.error &&
                      "aero:border-destructive aero:ring-destructive",
                    triggerClassName,
                  )}
                >
                  <span
                    className={cn(
                      "aero:truncate",
                      field.value
                        ? "aero:text-foreground"
                        : "aero:text-neutral-500",
                    )}
                  >
                    {getOptionLabel(field.value) || placeholder}
                  </span>
                  <div className="aero:flex aero:items-center aero:gap-1 aero:flex-shrink-0">
                    {allowClear && field.value > 0 && (
                      <span
                        role="button"
                        className="aero:p-0.5 aero:rounded-full aero:hover:bg-neutral-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          field.onChange(null);
                          if (onValueChange) {
                            onValueChange(null);
                          }
                          setSearch("");
                        }}
                      >
                        <X className="aero:h-3 aero:w-3 aero:text-neutral-500" />
                      </span>
                    )}
                    <ChevronsUpDown className="aero:h-5 aero:w-5 aero:text-neutral-500" />
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent
                className={cn(
                  "aero:w-(--radix-popover-trigger-width) aero:p-0",
                  contentClassName,
                )}
                align="start"
              >
                <div className="aero:flex aero:items-center aero:border-b aero:px-3">
                  <Search className="aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="aero:h-9 aero:border-0 aero:shadow-none aero:focus-visible:ring-0"
                  />
                  {search && (
                    <span
                      role="button"
                      className="aero:p-1 aero:rounded-full aero:hover:bg-muted aero:cursor-pointer"
                      onClick={() => setSearch("")}
                    >
                      <X className="aero:h-3 aero:w-3 aero:opacity-50" />
                    </span>
                  )}
                </div>
                <ScrollArea className="aero:h-60">
                  {filteredOptions.length === 0 ? (
                    <div className="aero:m-2 aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-2 aero:rounded aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:py-2 aero:px-2 aero:h-56">
                      <span className="aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-center aero:text-foreground">
                        No results found
                      </span>
                      {onAdd && (
                        <Button
                          type="button"
                          size="sm"
                          className="aero:h-9 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:bg-blue-800 aero:hover:bg-blue-900"
                          onClick={() => {
                            setAddDialogInitialValue(search);
                            setAddDialogOpen(true);
                            setOpen(false);
                          }}
                        >
                          <Plus className="aero:h-4 aero:w-4" />
                          {addButtonLabel}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="aero:p-1">
                      {filteredOptions.map((option) => (
                        <div
                          key={option.value}
                          role="option"
                          aria-selected={
                            field.value?.toString() === option.value.toString()
                          }
                          className={cn(
                            "aero:relative aero:flex aero:cursor-pointer aero:select-none aero:items-center aero:rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-none",
                            "aero:hover:bg-neutral-100 aero:hover:text-foreground",
                            field.value?.toString() ===
                              option.value.toString() &&
                              "aero:bg-brand-primary-50 aero:text-brand-primary-600",
                          )}
                          onClick={() => {
                            const newValue = option.value.toString();
                            field.onChange(newValue);
                            if (onValueChange) {
                              onValueChange(newValue);
                            }
                            setOpen(false);
                            setSearch("");
                          }}
                        >
                          <span className="aero:truncate">{option.label}</span>
                          {field.value?.toString() ===
                            option.value.toString() && (
                            <Check className="aero:ml-auto aero:h-4 aero:w-4" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </FormControl>
          {description && (
            <FormDescription className={descriptionClassName}>
              {description}
            </FormDescription>
          )}
          <div className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}>
            <FormMessage
              className={cn(
                fieldState?.error
                  ? "aero:text-destructive aero:text-xs"
                  : preserveMessageSpace
                    ? "aero:invisible aero:text-xs"
                    : "aero:hidden",
                messageClassName,
              )}
            />
          </div>

          {/* Add Option Dialog */}
          {onAdd && (
            <AddOptionDialog
              open={addDialogOpen}
              onOpenChange={setAddDialogOpen}
              title={addDialogTitle || `Add ${label}`}
              inputLabel={addDialogInputLabel || label}
              initialValue={addDialogInitialValue}
              isLoading={isAddLoading}
              onSave={(value) => {
                const resetValue = () => {
                  setAddDialogInitialValue("");
                  setAddDialogOpen(false);
                  setOpen(false);
                };

                onAdd(value, resetValue);
                setSearch("");
              }}
            />
          )}
        </FormItem>
      )}
      {...props}
    />
  );
};
